import { motion } from 'framer-motion';

const DIMENSIONS = [
  { key: 'patience', label: 'Kesabaran' },
  { key: 'risk_tolerance', label: 'Toleransi Risiko' },
  { key: 'discipline', label: 'Disiplin' },
  { key: 'time_commitment', label: 'Komitmen Waktu' },
  { key: 'decision_speed', label: 'Kecepatan' },
  { key: 'emotional_control', label: 'Kontrol Emosi' },
];

const getGrade = (value) => {
  if (value >= 80) return 'A';
  if (value >= 60) return 'B';
  if (value >= 40) return 'C';
  if (value >= 20) return 'D';
  return 'E';
};

const GRADE_LEVELS = ['A', 'B', 'C', 'D', 'E'];
const GRADE_RADII = [1, 0.8, 0.6, 0.4, 0.2];

const TraderStatsChart = ({ riskProfile, accentColor = '#FF6B6B', traderType = '' }) => {
  const cx = 200;
  const cy = 200;
  const outerRadius = 150;
  const ringWidth = 20;
  const tickCount = 30;
  const angleStep = (2 * Math.PI) / DIMENSIONS.length;
  const startAngle = -Math.PI / 2;

  const polarToCartesian = (angle, radius) => ({
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  });

  const getValueRadius = (value) => (value / 100) * outerRadius;

  // Build the filled polygon points
  const polygonPoints = DIMENSIONS.map((dim, i) => {
    const angle = startAngle + i * angleStep;
    const value = riskProfile[dim.key] || 0;
    const r = getValueRadius(value);
    const pt = polarToCartesian(angle, r);
    return `${pt.x},${pt.y}`;
  }).join(' ');

  // Outer ring tick marks
  const ticks = [];
  for (let i = 0; i < tickCount; i++) {
    const angle = (2 * Math.PI * i) / tickCount;
    const inner = polarToCartesian(angle, outerRadius + 4);
    const outer = polarToCartesian(angle, outerRadius + ringWidth);
    const isAxis = i % (tickCount / DIMENSIONS.length) === 0;
    ticks.push(
      <line
        key={`tick-${i}`}
        x1={inner.x} y1={inner.y}
        x2={outer.x} y2={outer.y}
        stroke={isAxis ? '#fff' : '#555'}
        strokeWidth={isAxis ? 2.5 : 1.2}
      />
    );
  }

  return (
    <div className="relative w-full flex justify-center">
      <svg viewBox="0 0 400 400" className="w-full max-w-[400px]">
        <defs>
          <radialGradient id={`glow-${traderType}`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={accentColor} stopOpacity="0.15" />
            <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
          </radialGradient>
          <filter id="polygon-glow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background glow */}
        <circle cx={cx} cy={cy} r={outerRadius + ringWidth + 10} fill={`url(#glow-${traderType})`} />

        {/* Outer ring background */}
        <circle
          cx={cx} cy={cy}
          r={outerRadius + ringWidth / 2 + 2}
          fill="none"
          stroke="#222"
          strokeWidth={ringWidth + 4}
        />
        <circle
          cx={cx} cy={cy}
          r={outerRadius + ringWidth / 2 + 2}
          fill="none"
          stroke="#333"
          strokeWidth={ringWidth}
        />

        {/* Tick marks on outer ring */}
        {ticks}

        {/* Inner circle background */}
        <circle cx={cx} cy={cy} r={outerRadius} fill="#0a0a0a" stroke="#333" strokeWidth={1.5} />

        {/* Concentric grade rings */}
        {GRADE_RADII.map((ratio, idx) => (
          <circle
            key={`ring-${idx}`}
            cx={cx} cy={cy}
            r={outerRadius * ratio}
            fill="none"
            stroke="#1a1a1a"
            strokeWidth={0.8}
          />
        ))}

        {/* Axis lines + grade labels + dimension labels + grade letters */}
        {DIMENSIONS.map((dim, i) => {
          const angle = startAngle + i * angleStep;
          const axisEnd = polarToCartesian(angle, outerRadius);
          const value = riskProfile[dim.key] || 0;
          const grade = getGrade(value);

          // Label position (outside the outer ring)
          const labelPos = polarToCartesian(angle, outerRadius + ringWidth + 28);

          // Grade letter position (between ring and label)
          const gradePos = polarToCartesian(angle, outerRadius + ringWidth + 12);

          // Text anchor based on angle position
          let textAnchor = 'middle';
          const angleDeg = ((angle * 180) / Math.PI + 360) % 360;
          if (angleDeg > 30 && angleDeg < 150) textAnchor = 'start';
          if (angleDeg > 210 && angleDeg < 330) textAnchor = 'end';

          return (
            <g key={dim.key}>
              {/* Axis line */}
              <line
                x1={cx} y1={cy}
                x2={axisEnd.x} y2={axisEnd.y}
                stroke="#333"
                strokeWidth={1}
              />

              {/* Small ticks on axis */}
              {GRADE_RADII.map((ratio, gIdx) => {
                const tickPos = polarToCartesian(angle, outerRadius * ratio);
                const perpAngle = angle + Math.PI / 2;
                const t1 = {
                  x: tickPos.x + 3 * Math.cos(perpAngle),
                  y: tickPos.y + 3 * Math.sin(perpAngle),
                };
                const t2 = {
                  x: tickPos.x - 3 * Math.cos(perpAngle),
                  y: tickPos.y - 3 * Math.sin(perpAngle),
                };
                return (
                  <line
                    key={`axis-tick-${dim.key}-${gIdx}`}
                    x1={t1.x} y1={t1.y}
                    x2={t2.x} y2={t2.y}
                    stroke="#444"
                    strokeWidth={0.8}
                  />
                );
              })}

              {/* Grade letter (large, bold) */}
              <text
                x={gradePos.x}
                y={gradePos.y}
                textAnchor="middle"
                dominantBaseline="central"
                fill="#fff"
                fontSize="18"
                fontWeight="900"
                fontFamily="system-ui, sans-serif"
              >
                {grade}
              </text>

              {/* Dimension label */}
              <text
                x={labelPos.x}
                y={labelPos.y}
                textAnchor={textAnchor}
                dominantBaseline="central"
                fill="#888"
                fontSize="10"
                fontWeight="600"
                fontFamily="system-ui, sans-serif"
                textTransform="uppercase"
                letterSpacing="0.05em"
              >
                {dim.label.toUpperCase()}
              </text>
            </g>
          );
        })}

        {/* Grade scale labels along the top axis (12 o'clock) */}
        {GRADE_LEVELS.map((grade, idx) => {
          const y = cy - outerRadius * GRADE_RADII[idx];
          return (
            <text
              key={`scale-${grade}`}
              x={cx + 8}
              y={y - 2}
              fill="#555"
              fontSize="9"
              fontWeight="700"
              fontFamily="system-ui, sans-serif"
            >
              {grade}
            </text>
          );
        })}

        {/* Filled polygon (the stats shape) */}
        <motion.polygon
          points={polygonPoints}
          fill={accentColor}
          fillOpacity={0.55}
          stroke={accentColor}
          strokeWidth={2.5}
          strokeLinejoin="round"
          filter="url(#polygon-glow)"
          initial={{ opacity: 0, scale: 0.3 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.3 }}
          style={{ transformOrigin: `${cx}px ${cy}px` }}
        />

        {/* Data points on polygon vertices */}
        {DIMENSIONS.map((dim, i) => {
          const angle = startAngle + i * angleStep;
          const value = riskProfile[dim.key] || 0;
          const r = getValueRadius(value);
          const pt = polarToCartesian(angle, r);
          return (
            <motion.circle
              key={`dot-${dim.key}`}
              cx={pt.x}
              cy={pt.y}
              r={4}
              fill="#fff"
              stroke={accentColor}
              strokeWidth={2}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.6 + i * 0.08 }}
            />
          );
        })}

        {/* Center dot */}
        <circle cx={cx} cy={cy} r={3} fill="#333" />
      </svg>
    </div>
  );
};

export default TraderStatsChart;
